/**
 * frontend/src/pages/PlaylistEdit.jsx — /playlists/:id
 * SoundDAO Sprint 6A — rename, describe, public/private, ↑/↓ reorder (no drag library),
 * remove, Share (copies the public URL; the toast shows the SOUND the server actually
 * paid, and only when the response says awarded), delete.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  deletePlaylist, formatDuration, myPlaylists, playlistToQueue, removeTrack, reorderTracks, sharePlaylist, updatePlaylist,
} from '../lib/archiveApi';
import { usePlayer } from '../player/PlayerContext';
import Toast from '../components/archive/Toast';
import { useRates, sound } from '../lib/rates';
import { track } from '../lib/analytics';
import { PLAYLIST_SHARE_MIN_TRACKS as SHARE_MIN } from '../lib/constants';
import '../styles/archive.css';

const MAX = 50;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function PlaylistEdit() {
  const rates = useRates();
  const { id } = useParams();
  const navigate = useNavigate();
  const { playQueue, isCurrent, toggle, status } = usePlayer();
  const [pl, setPl] = useState(null);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const adopt = (p) => {
    setPl(p);
    setTitle(p.title);
    setDesc(p.description || '');
  };

  const load = useCallback(() => {
    setError(null);
    myPlaylists()
      .then((d) => {
        const p = (d.playlists || []).find((x) => x.id === id);
        if (p) adopt(p);
        else setError({ status: 404, message: 'Playlist not found.' });
      })
      .catch((e) => setError(e));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const clearToast = useCallback(() => setToast(null), []);
  const fail = (e) => setToast({ type: 'error', text: e.message || 'Something went wrong.' });

  const saveFields = async (fields) => {
    try {
      const { playlist } = await updatePlaylist(id, fields);
      adopt(playlist);
    } catch (e) {
      fail(e);
      if (pl) { setTitle(pl.title); setDesc(pl.description || ''); }
    }
  };
  const saveTitle = () => {
    const t = title.trim();
    if (!pl || t === pl.title) return;
    if (!t) { setTitle(pl.title); return; }
    saveFields({ title: t });
  };
  const saveDesc = () => {
    if (!pl || desc.trim() === (pl.description || '')) return;
    saveFields({ description: desc.trim() || null });
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (!pl || j < 0 || j >= pl.tracks.length || busy) return;
    const next = [...pl.tracks];
    [next[i], next[j]] = [next[j], next[i]];
    setPl({ ...pl, tracks: next });   // optimistic
    setBusy(true);
    try {
      const { tracks } = await reorderTracks(id, next.map((t) => t.id));
      setPl((cur) => ({ ...cur, tracks }));
    } catch (e) {
      fail(e);
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (busy) return;
    setBusy(true);
    try {
      await removeTrack(id, t.id);
      setPl((cur) => ({ ...cur, tracks: cur.tracks.filter((x) => x.id !== t.id), trackCount: cur.trackCount - 1 }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!pl || busy) return;
    setBusy(true);
    try {
      const r = await sharePlaylist(id);
      track('playlist_shared', { awarded: Boolean(r.awarded) });
      const copied = await copyText(r.shareUrl);
      setCopiedUrl(copied ? r.shareUrl : null);
      if (r.awarded) {
        setToast({ text: `+${r.amount} SOUND · playlist shared${copied ? ' · link copied' : ''}` });
      } else {
        setToast({ type: 'info', text: copied ? 'Link copied · share reward already earned today' : 'Share reward already earned today' });
      }
      if (!copied) window.prompt('Copy your playlist link:', r.shareUrl);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    try {
      await deletePlaylist(id);
      navigate('/playlists', { replace: true });
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  };

  const queue = pl ? playlistToQueue(pl) : [];
  const shareBlocked = pl && (!pl.isPublic || pl.tracks.length < SHARE_MIN);
  const need = pl ? SHARE_MIN - pl.tracks.length : 0;

  return (
    <div className="ax-page">
      <div className="ax-wrap">
        <Link to="/playlists" className="ax-back">‹ My Playlists</Link>
        {error && <div className="ax-banner ax-banner--error"><span>{error.message}</span>{error.status !== 404 && <button type="button" className="ax-btn ax-btn--sm" onClick={load}>Retry</button>}</div>}
        {!pl && !error && <><div className="ax-skel" style={{ height: 60 }} /><div className="ax-skel" /><div className="ax-skel" /></>}
        {pl && (
          <div className="ax-two">
            <div>
              <input className="ax-input ax-input--title" value={title} maxLength={60} aria-label="Playlist title"
                onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />
              <textarea className="ax-input" style={{ marginTop: 8, fontSize: 14, resize: 'vertical' }} rows={2} value={desc} maxLength={280}
                placeholder="Description (optional)" aria-label="Description" onChange={(e) => setDesc(e.target.value)} onBlur={saveDesc} />
              <label className="ax-switch">
                <input type="checkbox" checked={pl.isPublic} onChange={(e) => saveFields({ is_public: e.target.checked })} />
                <span>{pl.isPublic ? 'Public — anyone with the link can listen' : 'Private — only you can see it'}</span>
              </label>

              <div className="ax-actions">
                <button type="button" className="ax-btn ax-btn--primary" onClick={share} disabled={busy || shareBlocked}>↗ Share · copy link</button>
                <button type="button" className="ax-btn" onClick={() => queue.length && playQueue(queue, 0)} disabled={!queue.length}>▶ Play all</button>
                <button type="button" className="ax-btn ax-btn--danger" onClick={doDelete} disabled={busy} onBlur={() => setConfirmDelete(false)}>
                  {confirmDelete ? 'Tap again to delete' : 'Delete'}
                </button>
              </div>
              <p className="ax-attr">
                {!pl.isPublic
                  ? 'Make it public to share.'
                  : need > 0
                    ? `Add ${need} more track${need === 1 ? '' : 's'} to share.`
                    : `Sharing earns ${sound(rates, 'playlist_share')} once a day (the same daily reward as linked playlists).`}
                {copiedUrl && <> Link copied: <a href={copiedUrl} target="_blank" rel="noopener noreferrer">{copiedUrl.replace('https://', '')}</a></>}
              </p>
            </div>

            <div>
              <h2 className="ax-h2">{pl.tracks.length} track{pl.tracks.length === 1 ? '' : 's'} · {MAX} max</h2>
              {pl.tracks.length === 0 && (
                <div className="ax-card ax-meta">Empty. <Link to="/dig" style={{ color: '#a5b4fc' }}>Dig the Archive</Link> and tap “+ Playlist” on a track.</div>
              )}
              {pl.tracks.map((t, i) => {
                const on = isCurrent(queue[i]?.key);
                const playing = on && (status === 'playing' || status === 'loading');
                return (
                  <div className="ax-track" key={t.id}>
                    <button type="button" className="ax-icon-btn" onClick={() => move(i, -1)} disabled={busy || i === 0} aria-label={`Move ${t.title} up`}>↑</button>
                    <button type="button" className="ax-icon-btn" onClick={() => move(i, 1)} disabled={busy || i === pl.tracks.length - 1} aria-label={`Move ${t.title} down`}>↓</button>
                    <button type="button" className={`ax-play ${on ? 'on' : ''}`} onClick={() => (on ? toggle() : playQueue(queue, i))} aria-label={playing ? `Pause ${t.title}` : `Play ${t.title}`}>
                      {playing ? '❚❚' : '▶'}
                    </button>
                    <div className="ax-grow">
                      <div className="ax-title">{t.title || t.fileName}</div>
                      <div className="ax-meta">{t.artistDisplay} · <a href={t.archiveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc', textDecoration: 'none' }}>archive.org ↗</a></div>
                    </div>
                    <span className="ax-dur">{formatDuration(t.durationSec)}</span>
                    <button type="button" className="ax-icon-btn" onClick={() => remove(t)} disabled={busy} aria-label={`Remove ${t.title}`}>✕</button>
                  </div>
                );
              })}
              {pl.tracks.length > 0 && <p className="ax-attr">Streaming from the Internet Archive · each track links to its archive.org page</p>}
            </div>
          </div>
        )}
      </div>
      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}
